/**
 * Persistence.
 *
 * The only file in the application that touches storage, local or remote. The
 * reducers in `@yuan-math/learning` are pure and storage-agnostic, so adding
 * server sync meant changing this file and nothing else.
 *
 * Two rules shape everything here:
 *
 * 1. **Local always wins on availability.** Every read and write hits
 *    `localStorage` first and succeeds without a network. A student on a train
 *    must never see a spinner, and a Supabase outage must never stop a lesson.
 * 2. **The server is a merge target, not a source of truth.** Two devices can
 *    both hold real work, so remote state is merged rather than applied — see
 *    `mergeProfiles`.
 *
 * Nothing here is collected about a child beyond what the platform needs to
 * teach them (§29): a display name they chose, an age band, and their own work.
 * No birthday, and nothing that identifies an individual learner to anyone else.
 */

import { STORAGE_KEY } from '@yuan-math/config';
import { mergeProfiles, type LearnerProfile } from '@yuan-math/learning';
import { supabaseClient } from './auth.ts';

type StoredEnvelope = {
  version: 1;
  profile: LearnerProfile;
};

/**
 * Resolves the browser's storage.
 *
 * Deliberately `window.localStorage` rather than the bare global: recent Node
 * versions expose their own `localStorage` global, which shadows the browser's
 * under test and is inert without a backing file.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const store = window.localStorage;
    // Feature-detect rather than trust the global: a shadowed or disabled
    // storage object can exist while having no usable methods.
    return typeof store?.getItem === 'function' ? store : null;
  } catch {
    // Accessing storage throws outright when cookies are blocked.
    return null;
  }
}

export function loadProfile(): LearnerProfile | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredEnvelope;
    if (parsed.version !== 1 || !parsed.profile?.id) return null;
    return normalise(parsed.profile);
  } catch {
    // Corrupt storage must never break the app; the student starts fresh.
    return null;
  }
}

export function saveProfile(profile: LearnerProfile): void {
  const store = storage();
  if (!store) return;
  try {
    const envelope: StoredEnvelope = { version: 1, profile };
    store.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Quota or private-mode failures are not worth interrupting a lesson for.
  }
}

/**
 * Removes the learner's profile from the server.
 *
 * Scoped by account id as well as profile id: the row-level security policy
 * already restricts this to the caller's own rows, and matching on both means a
 * mistaken id cannot reach somebody else's data even if that policy were ever
 * loosened.
 */
export async function deleteRemoteProfile(accountId: string, profileId: string): Promise<boolean> {
  const client = supabaseClient();
  if (!client) return false;
  try {
    const { error } = await client
      .from('learner_profiles')
      .delete()
      .eq('account_id', accountId)
      .eq('id', profileId);
    return !error;
  } catch {
    return false;
  }
}

/** Full deletion, as a data-deletion request would perform it (§29). */
export function clearProfile(): void {
  storage()?.removeItem(STORAGE_KEY);
}

/** Everything the platform holds about this learner, as a downloadable file. */
export function exportProfile(profile: LearnerProfile): string {
  return JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), profile }, null, 2);
}

/* -------------------------------------------------------------------------- */
/* Remote sync                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Reads the account's profile from Supabase.
 *
 * Returns null for every failure — no row yet, no network, no permission. A
 * failed sync is not an error state for the student; it just means the local
 * copy stands.
 */
export async function fetchRemoteProfile(accountId: string): Promise<LearnerProfile | null> {
  const client = supabaseClient();
  if (!client) return null;

  try {
    const { data, error } = await client
      .from('learner_profiles')
      .select('data')
      .eq('account_id', accountId)
      .maybeSingle();

    if (error || !data?.data) return null;
    const profile = data.data as LearnerProfile;
    return profile?.id ? normalise(profile) : null;
  } catch {
    return null;
  }
}

/**
 * Writes the profile to Supabase.
 *
 * Resolves `false` rather than throwing when the write fails: the caller keeps
 * the local copy and will try again on the next change.
 */
export async function pushRemoteProfile(accountId: string, profile: LearnerProfile): Promise<boolean> {
  const client = supabaseClient();
  if (!client) return false;

  try {
    const { error } = await client.from('learner_profiles').upsert(
      {
        id: profile.id,
        account_id: accountId,
        display_name: profile.displayName,
        age_band: profile.ageBand,
        level_id: profile.levelId ?? null,
        xp: profile.xp,
        data: profile,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' },
    );
    return !error;
  } catch {
    return false;
  }
}

/**
 * Reconciles local and remote on sign-in.
 *
 * This is the moment that matters: a student who practised as a guest, or on
 * another device, must find all of it afterwards. Merging rather than choosing
 * is what makes that true — and the merged result is pushed straight back so
 * both sides agree.
 */
export async function syncOnSignIn(accountId: string, local: LearnerProfile | null): Promise<LearnerProfile | null> {
  const remote = await fetchRemoteProfile(accountId);

  if (!remote) {
    // First device for this account. Adopt whatever is here, if anything.
    if (local) await pushRemoteProfile(accountId, local);
    return local;
  }
  if (!local) {
    saveProfile(remote);
    return remote;
  }

  const merged = mergeProfiles(local, remote);
  saveProfile(merged);
  await pushRemoteProfile(accountId, merged);
  return merged;
}

/** Fills in fields added by later versions, so old saves keep working. */
export function normalise(profile: LearnerProfile): LearnerProfile {
  const stats = profile.stats ?? { totalAttempts: 0, totalCorrect: 0, totalStudyMs: 0, lessonsCompleted: 0 };
  const streak = profile.streak ?? { current: 0, longest: 0, lastActiveDay: null, totalActiveDays: 0 };
  return {
    ...profile,
    concepts: profile.concepts ?? {},
    completedLessonIds: profile.completedLessonIds ?? [],
    achievements: profile.achievements ?? [],
    attempts: profile.attempts ?? [],
    stats: { ...stats, questsCompleted: stats.questsCompleted ?? 0, bestCombo: stats.bestCombo ?? 0 },
    streak: { ...streak, freezes: streak.freezes ?? 0, freezeUsedOn: streak.freezeUsedOn ?? null },
    // A daily record from an older client can be missing its newer counters,
    // and a half-filled one would make a quest bar read `NaN`. Repaired here
    // rather than defended against in nine display sites.
    ...(profile.daily ? { daily: normaliseDaily(profile.daily) } : {}),
  };
}

function normaliseDaily(daily: LearnerProfile['daily']): NonNullable<LearnerProfile['daily']> {
  const record = daily as NonNullable<LearnerProfile['daily']>;
  return {
    day: record.day,
    attempts: record.attempts ?? 0,
    correct: record.correct ?? 0,
    hardCorrect: record.hardCorrect ?? 0,
    unaidedCorrect: record.unaidedCorrect ?? 0,
    reviewAttempts: record.reviewAttempts ?? 0,
    lessonsCompleted: record.lessonsCompleted ?? 0,
    combo: record.combo ?? 0,
    bestCombo: record.bestCombo ?? 0,
    conceptIds: record.conceptIds ?? [],
    questIds: record.questIds ?? [],
    claimedQuestIds: record.claimedQuestIds ?? [],
  };
}
